import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const DashboardPreview = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section className="section-padding-mobile bg-white relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -left-24 w-48 h-48 xs:-left-48 xs:w-96 xs:h-96 bg-gradient-to-r from-primary-100 to-accent-100 rounded-full blur-3xl opacity-30"></div>
        <div className="absolute bottom-1/4 -right-24 w-48 h-48 xs:-right-48 xs:w-96 xs:h-96 bg-gradient-to-r from-accent-100 to-primary-100 rounded-full blur-3xl opacity-30"></div>
      </div>

      <div className="container-mobile relative">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 xs:mb-16 lg:mb-20"
        >

          <h2 id="dashboard-heading" className="text-mobile-title font-bold text-gray-900 mb-4 xs:mb-6">
            Dashboard{' '}
            <span className="gradient-text">Preview</span>
          </h2>
          
          <p className="text-mobile-body text-gray-600 max-w-4xl mx-auto">
            Experience our intuitive dashboard that transforms complex sensor data into actionable insights with real-time visualization
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="relative max-w-7xl mx-auto"
        >
          {/* Enhanced laptop mockup */}
          <div className="relative perspective-1000">
            {/* Laptop shadow */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-200/20 blur-2xl transform translate-y-4 xs:translate-y-8 scale-105"></div>
            
            {/* Laptop base */}
            <div className="relative bg-gradient-to-b from-gray-800 to-gray-900 rounded-xl xs:rounded-2xl p-3 xs:p-6 shadow-2xl transform rotate-x-5">
              {/* Screen bezel */}
              <div className="bg-gray-900 rounded-lg xs:rounded-xl p-2 xs:p-3 mb-2 xs:mb-4 shadow-inner">
                {/* Screen */}
                <div className="bg-white rounded-md xs:rounded-lg overflow-hidden shadow-2xl relative">
                  {/* Dashboard mockup image */}
                  <img 
                    src="/mockupimage.png" 
                    alt="CIVIONIKS Dashboard Preview" 
                    className="w-full h-auto object-cover rounded-md xs:rounded-lg"
                  />
                </div>
              </div>
              
              {/* Laptop keyboard */}
              <div className="h-3 xs:h-6 bg-gradient-to-b from-gray-700 to-gray-800 rounded-lg mx-4 xs:mx-8 shadow-inner"></div>
            </div>
            
            {/* Laptop stand */}
            <div className="h-2 xs:h-4 bg-gradient-to-b from-gray-600 to-gray-700 rounded-b-xl mx-auto shadow-lg" style={{ width: '60%' }}></div>
          </div>

          {/* Enhanced feature callouts with arrows */}
          <div className="grid-mobile-3 gap-6 xs:gap-8 mt-12 xs:mt-16">
            {[
              {
                title: 'Real-time Metrics',
                description: 'Live sensor data updates every second with intelligent filtering and anomaly detection',
                icon: '⚡',
                color: 'from-blue-500 to-primary-600'
              },
              {
                title: 'Smart Alerts',
                description: 'AI-powered notifications for critical events with customizable thresholds and escalation',
                icon: '🔔',
                color: 'from-orange-500 to-red-600'
              },
              {
                title: 'Project Health',
                description: 'Comprehensive status summaries with predictive maintenance and compliance tracking',
                icon: '📊',
                color: 'from-green-500 to-emerald-600'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                className="text-center group"
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 xs:w-14 xs:h-14 bg-gradient-to-r ${feature.color} rounded-xl xs:rounded-2xl text-white text-xl xs:text-2xl mb-3 xs:mb-4 group-hover:scale-110 transition-transform duration-300 shadow-mobile-medium xs:shadow-medium`}>
                  {feature.icon}
                </div>
                <h3 className="text-primary-600 font-semibold text-base xs:text-lg mb-2 group-hover:text-primary-700 transition-colors duration-200">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Section divider */}
      <div className="section-divider mt-16 xs:mt-20 lg:mt-24"></div>
    </section>
  );
};

export default DashboardPreview; 